import type { SiteBundleDto } from '@portfolio/shared';
import { Reveal } from '../Reveal';
import { SocialIcon, platformName } from '../SocialIcon';

export function Intro({ data }: { data: SiteBundleDto }) {
  const s = data.settings;
  const [first, ...rest] = s.ownerName.split(' ');
  return (
    <section id="about" aria-labelledby="about-title" className="container-site pt-16 md:pt-[60px]">
      <div className="grid gap-x-10 gap-y-10 lg:grid-cols-[minmax(0,291fr)_minmax(0,326fr)_minmax(0,511fr)] lg:gap-x-0 lg:gap-y-[88px]">
        <div>
          <Reveal>
            <h2
              id="about-title"
              className="text-[clamp(2.4rem,1.6rem+2.4vw,3rem)] leading-none font-bold tracking-normal"
            >
              {first}
              {rest.length > 0 && (
                <>
                  <br />
                  {rest.join(' ')}
                </>
              )}
            </h2>
          </Reveal>
          {s.ownerTagline && (
            <Reveal delay={0.1}>
              <p className="mt-3 text-lead font-light text-silver">{s.ownerTagline}</p>
            </Reveal>
          )}
        </div>
        <div>
          <Reveal delay={0.1}>
            <p className="pt-1 text-lead font-light whitespace-pre-line text-ink lg:pt-[27px] lg:pe-6">
              {s.roles}
            </p>
          </Reveal>
          {s.location && (
            <Reveal delay={0.2}>
              <p className="mt-3 text-caption text-muted">{s.location}</p>
            </Reveal>
          )}
        </div>
        <Reveal delay={0.2}>
          <p className="text-body text-graphite lg:pt-[27px] lg:text-justify">{s.bioPrimary}</p>
        </Reveal>

        {/* Stacked (below lg): the social links close the section, after the education and bio */}
        <div className="max-lg:order-1">
          {data.socials.length > 0 && (
            // Stacked, the first link's top padding would add to the row gap above it
            <ul className="max-w-[161px] divide-y divide-line max-lg:-mt-[15px]">
              {data.socials.map((l, i) => (
                <Reveal as="li" key={l.id} delay={i * 0.1}>
                  <a
                    href={l.url}
                    target={l.url.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 py-[15px] text-ui text-graphite transition-colors hover:text-primary-deep"
                    aria-label={`${platformName[l.platform]}: ${l.label}`}
                  >
                    <SocialIcon platform={l.platform} className="size-[22px] text-ink" />
                    {l.label}
                  </a>
                </Reveal>
              ))}
            </ul>
          )}
          {(s.contactEmail || s.contactPhone) && (
            <Reveal
              delay={data.socials.length * 0.1}
              className="mt-4 space-y-1 text-caption text-graphite"
            >
              {s.contactEmail && (
                <a
                  dir="ltr"
                  className="block text-start hover:text-primary-deep rtl:text-end"
                  href={`mailto:${s.contactEmail}`}
                >
                  {s.contactEmail}
                </a>
              )}
              {s.contactPhone && (
                <a
                  dir="ltr"
                  className="block text-start hover:text-primary-deep rtl:text-end"
                  href={`tel:${s.contactPhone.replace(/\s/g, '')}`}
                >
                  {s.contactPhone}
                </a>
              )}
            </Reveal>
          )}
        </div>
        {/* Empty cells are skipped entirely when stacked, so they don't add a second row gap */}
        <Reveal delay={0.1} className={s.education ? undefined : 'max-lg:hidden'}>
          {s.education && (
            <p className="text-lead font-light whitespace-pre-line text-ink lg:pe-6">
              {s.education}
            </p>
          )}
        </Reveal>
        <Reveal delay={0.2} className={s.bioSecondary ? undefined : 'max-lg:hidden'}>
          {s.bioSecondary && (
            <p className="text-body text-graphite lg:text-justify">{s.bioSecondary}</p>
          )}
        </Reveal>
      </div>
      <div className="mt-16 h-px bg-line md:mt-[89px]" />
    </section>
  );
}
